
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getActualLastSyncTime } from '@/lib/wearables/sync-status-utils';

export interface WearableConnection {
  id: string;
  user_id: string;
  provider: string;
  access_token: string;
  refresh_token: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  last_sync_date: string | null;
  last_successful_sync: string | null;
  last_refresh_attempt: string | null;
  refresh_failure_count: number | null;
  token_health_status: string | null;
  initial_sync_completed: boolean | null;
}

export const useWearableConnections = () => {
  return useQuery({
    queryKey: ['wearable-connections'],
    queryFn: async (): Promise<WearableConnection[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('wearable_connections')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });
};

export const useActualLastSyncTime = (connection: WearableConnection | null) => {
  return useQuery({
    queryKey: ['actual-last-sync-time', connection?.id],
    queryFn: async (): Promise<string | null> => {
      if (!connection) return null;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      return await getActualLastSyncTime(connection, user.id);
    },
    enabled: !!connection,
  });
};

export const useUpsertWearableConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      provider,
      accessToken,
      refreshToken,
      expiresIn,
    }: {
      provider: string;
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase.rpc('upsert_wearable_connection', {
        p_user_id: user.id,
        p_provider: provider,
        p_access_token: accessToken,
        p_refresh_token: refreshToken,
        p_expires_in: expiresIn,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wearable-connections'] });
      toast({
        title: 'Success',
        description: 'Wearable device connected successfully',
      });
    },
    onError: (error) => {
      console.error('Failed to connect wearable:', error);
      toast({
        title: 'Error',
        description: 'Failed to connect wearable device',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteWearableConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      console.log('🗑️ Deleting wearable connection:', connectionId);
      
      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('❌ User not authenticated for delete:', authError);
        throw new Error('User not authenticated');
      }
      
      console.log('✅ User authenticated:', user.id);
      
      // First check if the connection exists and belongs to the user
      const { data: connection, error: checkError } = await supabase
        .from('wearable_connections')
        .select('id, user_id, provider')
        .eq('id', connectionId)
        .single();
      
      if (checkError) {
        console.error('❌ Error checking connection:', checkError);
        throw checkError;
      }
      
      if (!connection) {
        console.error('❌ Connection not found');
        throw new Error('Connection not found');
      }
      
      if (connection.user_id !== user.id) {
        console.error('❌ Connection does not belong to user');
        throw new Error('Connection does not belong to user');
      }
      
      console.log('✅ Connection verified:', connection);
      
      const { error } = await supabase
        .from('wearable_connections')
        .delete()
        .eq('id', connectionId);

      if (error) {
        console.error('❌ Delete error:', error);
        throw error;
      }
      
      console.log('✅ Delete successful');
    },
    onSuccess: () => {
      console.log('🔄 Invalidating wearable connections cache');
      queryClient.invalidateQueries({ queryKey: ['wearable-connections'] });
      toast({
        title: 'Success',
        description: 'Wearable device disconnected',
      });
    },
    onError: (error) => {
      console.error('Failed to disconnect wearable:', error);
      toast({
        title: 'Error',
        description: 'Failed to disconnect wearable device',
        variant: 'destructive',
      });
    },
  });
};
